import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import BreakdownDialogTitle from "../pricing-reports/BreakdownDialogTitle";
import GalleryObservationThumb from "../pricing-reports/GalleryObservationThumb";
import PriceHistoryRecordPanel from "../pricing-reports/PriceHistoryRecordPanel";
import ProductImageViewer from "../pricing-reports/ProductImageViewer";
import { cadCurrencyFormat } from "../pricing-reports/constants";
import { withDirectusAssetTransformKey } from "../pricing-reports/pricingReportImageUrls";
import { formatAnomalyAlertLines } from "./verifyPriceAnomalies";
import VerifyPriceGlancePrices from "./VerifyPriceGlancePrices";
import { isEditableKeyboardTarget } from "./verifyPriceUtils";

function ShortcutKey({ children }) {
  return (
    <Box
      component="kbd"
      sx={{
        ml: 1,
        px: 0.75,
        minWidth: "1.35em",
        fontSize: "0.7rem",
        fontWeight: 700,
        fontFamily: "inherit",
        lineHeight: 1.4,
        textAlign: "center",
        border: "1px solid currentColor",
        borderRadius: 0,
        opacity: 0.8,
      }}
    >
      {children}
    </Box>
  );
}

function buildThumbs(item) {
  if (!item) return [];
  if (item.images?.length) {
    return item.images.map((url, imageIndex) => ({
      key: `${item.id}:${imageIndex}`,
      url,
      thumbnailUrl: withDirectusAssetTransformKey(url),
      label: item.recordAt,
      parentItem: item,
      imageIndex,
      isPlaceholder: false,
    }));
  }
  return [
    {
      key: `${item.id}:none`,
      url: null,
      label: item.recordAt,
      parentItem: item,
      imageIndex: null,
      isPlaceholder: true,
    },
  ];
}

export default function VerifyPriceReviewDialog({
  open,
  item,
  panelMode,
  onPanelModeChange,
  actionLoading,
  actionError,
  onClose,
  onAccept,
  onReject,
  onRestore,
  onSkip,
  onSaveRecord,
  productFilterChoices,
  retailerChoices,
  provinceChoices,
  unitChoices,
  formatProductOptionLabel,
  isPublishedProductsLoading,
  isLoadingFilterOptions,
  resolveProvinceForApi,
}) {
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down("sm"));
  const isStackedLayout = useMediaQuery(theme.breakpoints.down("md"));
  const thumbs = useMemo(() => buildThumbs(item), [item]);
  const anomalyLines = useMemo(
    () => formatAnomalyAlertLines(item?.anomaly, (value) => cadCurrencyFormat.format(value)),
    [item?.anomaly]
  );
  const [selectedThumbKey, setSelectedThumbKey] = useState("");
  const selectedThumb = thumbs.find((thumb) => thumb.key === selectedThumbKey) || thumbs[0] || null;
  const isEditMode = panelMode === "edit";
  const isVoided = Boolean(item?.voided);

  useEffect(() => {
    setSelectedThumbKey(thumbs[0]?.key || "");
  }, [item?.id, thumbs]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (actionLoading) return;
      if (isEditableKeyboardTarget(event.target)) return;
      if (isEditMode) return;

      const key = event.key;
      if (key === "a" || key === "A") {
        event.preventDefault();
        if (isVoided) onRestore();
        else onAccept();
        return;
      }
      if (key === "r" || key === "R") {
        if (isVoided) return;
        event.preventDefault();
        onReject();
        return;
      }
      if (key === "s" || key === "S") {
        event.preventDefault();
        onSkip(1);
        return;
      }
      if (key === "ArrowRight") {
        event.preventDefault();
        onSkip(1);
        return;
      }
      if (key === "ArrowLeft") {
        event.preventDefault();
        onSkip(-1);
        return;
      }
      if (key === " ") {
        event.preventDefault();
        onPanelModeChange("edit");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, actionLoading, isEditMode, isVoided, onAccept, onReject, onRestore, onSkip, onPanelModeChange]);

  return (
    <Dialog
      className="insights-dialog"
      open={open}
      onClose={actionLoading ? undefined : onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobileViewport}
      PaperProps={{
        sx: {
          height: isMobileViewport ? "100%" : "94vh",
          maxHeight: "94vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <BreakdownDialogTitle
        title={item?.product || "Verify price"}
        onClose={actionLoading ? undefined : onClose}
      />
      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          overflow: isStackedLayout ? "auto" : "hidden",
        }}
      >
        <Stack spacing={2} sx={{ minHeight: 0, flex: 1 }}>
          {actionError ? <Alert severity="error">{actionError}</Alert> : null}
          {anomalyLines.length ? (
            <Alert severity="warning">
              <Stack spacing={0.5}>
                {anomalyLines.map((line) => (
                  <Typography key={line} variant="body2">
                    {line}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          ) : null}
          <Grid container spacing={2} sx={{ minHeight: 0, flex: 1 }}>
            <Grid item xs={12} md={6}>
              <Stack spacing={1.5}>
                {selectedThumb?.url ? (
                  <ProductImageViewer
                    src={selectedThumb.url}
                    alt={`${item?.product || "Price record"} image`}
                    minHeight={isStackedLayout ? 240 : 480}
                    maxHeight={isStackedLayout ? 320 : 620}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      minHeight: isStackedLayout ? 220 : 480,
                      border: "1px solid #333",
                      backgroundColor: "#000",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2" color="grey.400">
                      No image for this record
                    </Typography>
                  </Box>
                )}

                {thumbs.length > 1 ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ overflowX: "auto", pb: 1 }}
                    role="list"
                    aria-label="Record images"
                  >
                    {thumbs.map((thumb) => (
                      <GalleryObservationThumb
                        key={thumb.key}
                        thumb={thumb}
                        isSelected={thumb.key === selectedThumb?.key}
                        onSelect={setSelectedThumbKey}
                      />
                    ))}
                  </Stack>
                ) : null}
              </Stack>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                minHeight: 0,
                maxHeight: isStackedLayout ? "none" : 620,
                overflow: "auto",
              }}
            >
              <Stack spacing={2}>
                <VerifyPriceGlancePrices item={item} />
                <PriceHistoryRecordPanel
                  item={item}
                  initialPanelMode={panelMode}
                  productFilterChoices={productFilterChoices}
                  retailerChoices={retailerChoices}
                  provinceChoices={provinceChoices}
                  unitChoices={unitChoices}
                  formatProductOptionLabel={formatProductOptionLabel}
                  isPublishedProductsLoading={isPublishedProductsLoading}
                  isLoadingFilterOptions={isLoadingFilterOptions}
                  resolveProvinceForApi={resolveProvinceForApi}
                  onSaveRecord={onSaveRecord}
                />
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", py: 2, px: 3, gap: 2, flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          size="large"
          onClick={() => onSkip(-1)}
          disabled={actionLoading || !item}
          aria-label="Previous (Left arrow)"
          sx={{ minWidth: 140 }}
        >
          Previous
          <ShortcutKey>←</ShortcutKey>
        </Button>
        <Button
          variant="outlined"
          size="large"
          onClick={() => onSkip(1)}
          disabled={actionLoading || !item}
          aria-label="Skip (S or Right arrow)"
          sx={{ minWidth: 140 }}
        >
          Skip
          <ShortcutKey>S</ShortcutKey>
          <ShortcutKey>→</ShortcutKey>
        </Button>
        <Button
          variant="outlined"
          size="large"
          onClick={() => onPanelModeChange("edit")}
          disabled={actionLoading || !item || isEditMode}
          aria-label="Edit (Space)"
          sx={{ minWidth: 140 }}
        >
          Edit
          <ShortcutKey>Space</ShortcutKey>
        </Button>
        {isVoided ? (
          <Button
            variant="contained"
            size="large"
            onClick={onRestore}
            disabled={actionLoading || !item}
            aria-label="Restore (A)"
            sx={{ minWidth: 160 }}
          >
            Restore
            <ShortcutKey>A</ShortcutKey>
          </Button>
        ) : (
          <>
            <Button
              variant="contained"
              size="large"
              onClick={onAccept}
              disabled={actionLoading || !item}
              aria-label="Accept (A)"
              sx={{ minWidth: 160 }}
            >
              Accept
              <ShortcutKey>A</ShortcutKey>
            </Button>
            <Button
              color="error"
              variant="outlined"
              size="large"
              onClick={onReject}
              disabled={actionLoading || !item}
              aria-label="Reject (R)"
              sx={{ minWidth: 140 }}
            >
              Reject
              <ShortcutKey>R</ShortcutKey>
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
